import { type JSX } from 'react';

/**
 * Branded cold-boot surface (#259) — the single restrained, operator-grade dark
 * splash SHARED by the auth-probe gate (AuthGate) and the globe-loading overlay
 * (GlobeBootOverlay), so the whole cold boot — auth probe → COP mounts → globe
 * imagery streams — reads as ONE continuous branded screen instead of three
 * different blanks.
 *
 * Doctrine (design-council-2026-06-05 + operator-cop-doctrine §3A.8 / §3A.3):
 *   - Dark ops-room palette only (bg-slate-950 over the #0b1020 document base).
 *     No logo asset exists in the app — the brand is the "TSR Platform" text
 *     wordmark (mirrors the SidePanel header treatment).
 *   - A 1–10s cold boot ⇒ ONE indeterminate spinner (§3A.8), nothing more.
 *     Motion is scarce: the spinner animates only under `motion-safe`, so a
 *     reduced-motion operator sees a static, readable ring (every keyframe in
 *     index.css carries the same reduced-motion guard).
 *   - Colour discipline (design-council Law 1): the indicator is neutral slate —
 *     NEVER red or green, both of which are reserved map-signal hues that would
 *     read as a hazard / live cue at boot.
 */
export function BootSplash({
  line = 'Assembling the operating picture…',
  testId = 'boot-splash',
  className = '',
}: {
  /** The status line under the wordmark. */
  line?: string;
  /** Stable hook for tests; defaults to `boot-splash`. */
  testId?: string;
  /** Extra classes for the root (e.g. opacity/positioning from a wrapper). */
  className?: string;
}): JSX.Element {
  return (
    <div
      data-testid={testId}
      role="status"
      aria-live="polite"
      className={`flex h-full w-full flex-col items-center justify-center gap-5 bg-slate-950 text-slate-300 ${className}`.trim()}
    >
      <div className="flex flex-col items-center gap-1">
        <span className="text-xl font-semibold tracking-tight text-slate-100">TSR Platform</span>
        <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
          Common Operating Picture
        </span>
      </div>
      <span
        className="h-6 w-6 rounded-full border-2 border-slate-600 border-t-transparent motion-safe:animate-spin"
        aria-hidden="true"
      />
      <p className="text-sm text-slate-400">{line}</p>
    </div>
  );
}
